import React from "react";
import { useForm, Controller, useFieldArray, FormProvider } from "react-hook-form";
import { Check, Loader2, Paperclip, Settings } from "lucide-react";
import { Label } from "@radix-ui/react-label";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { GroupBase, SelectComponentsConfig as SelectComponentsType } from "react-select";
import {
  useCreateSnapshotEventMutation,
  useUpdateSnapshotEventMutation,
  useLazySuggestBusinessReportOutcomeResultNameQuery,
  useGetSnapshotEventMailsQuery,
} from "@/modules/snapshots/businessReports.api";
import { clsx } from "clsx";
import { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import { RichTextEditor } from "@/components/ui/richTextEditor";
import { SelectComponents } from "@/components/share/components/SelectUsersComponents";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { DirectoryUser } from "@/types/user";
import * as analytics from "@/services/analytics";
import { SnapshotEventReminderForm } from "@/modules/snapshots/snapshotEvents/components/snapshotEventReminderForm";
import { SnapshotEventMailModal } from "@/modules/snapshots/snapshotEvents/snapshotEventMailModal";
import { format, parseISO } from "date-fns";
import { ReminderSimpleForm } from "@/modules/snapshots/snapshotEvents/components/reminderSimpleForm";
import {
  computeReminderStartDate,
  computeReminderOffset,
} from "@/modules/snapshots/snapshotEvents/helpers/computeReminderStartDate";
import type { SnapshotEventReminderFrequency } from "@/modules/snapshots/businessReports.types";
import { DatePicker } from "@/components/ui/Datepicker";

interface FormValues {
  emailMessage: string;
  emailSubject: string;
  eventDate: Date;
  eventTitle: string;
  includeAttachment: boolean;
  onePersonCompletion: boolean;
  recipients: { email: string; checked: boolean }[];
  recipientsText: string;

  reminderInterval: number;
  reminderFrequency: string;
  reminderEndAfterOccurrences: number | null;
  reminderWeekDays: Array<string>;
  reminderEndDate: string | null;

  sendReminderBefore: number | null;
  sendReminderBeforeFrequency: SnapshotEventReminderFrequency;

  repeatReminder: boolean;
  requiredOnlyOneDone: boolean;
}

// Fields shared by the create and update requests. Edit anchors the reminder start date on the
// reminder end date (falling back to the event date); create always anchors it on the event date.
const buildEventPayload = (data: FormValues, reminderStartAnchor: Date) => ({
  title: data.eventTitle,
  event_date: data.eventDate?.toISOString().slice(0, 10),
  reminder_interval: data.reminderInterval,
  reminder_frequency: data.reminderFrequency,
  reminder_week_days: data.reminderWeekDays,
  reminder_start_date: data.sendReminderBefore
    ? format(
        computeReminderStartDate(reminderStartAnchor, data.sendReminderBefore, data.sendReminderBeforeFrequency),
        "yyyy-MM-dd",
      )
    : null,
  reminder_end_date: data.reminderEndDate ? format(new Date(data.reminderEndDate), "yyyy-MM-dd") : null,
  email_subject: data.emailSubject,
  email_message: data.emailMessage,
  include_document_in_attachment: data.includeAttachment,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  required_only_one_done: data.recipients.filter(r => r.checked).length > 1 && data.onePersonCompletion,
  recipients: data.recipients.map(r => ({ email: r.email, is_readonly: !r.checked })),
});

interface Props {
  open: boolean;
  eventDate?: Date;
  onClose: () => void;
  mode: "create" | "edit";
  businessReportOutcomeResultId: string | null;
  businessReportOutcomeId: string;
  snapshotEvent?: GetSnapshotEventResponseItem;
  eventType?: string;
}

export const ModifySnapshotEventDialog: React.FC<Props> = ({
  open,
  mode,
  eventDate,
  businessReportOutcomeResultId,
  businessReportOutcomeId,
  snapshotEvent,
  onClose,
  eventType = "",
}) => {
  const [advancedReminderForm, setAdvancedReminderForm] = React.useState(
    mode === "edit" && !snapshotEvent?.reminder_start_date,
  );
  const [isNotesModalOpen, setIsNotesModalOpen] = React.useState(false);
  const userQuery = useGetDirectoryUsersQuery();
  const { data: snapshotEventMails = [] } = useGetSnapshotEventMailsQuery();
  const [createSnapshotEventMutation] = useCreateSnapshotEventMutation();
  const [updateSnapshotEventMutation] = useUpdateSnapshotEventMutation();
  const [triggerSuggestName, suggestNameQuery] = useLazySuggestBusinessReportOutcomeResultNameQuery();

  // Functions to manage advancedReminderForm state
  const enableAdvancedReminderForm = () => {
    setAdvancedReminderForm(true);

    methods.setValue("sendReminderBefore", null);
  };

  const disableAdvancedReminderForm = () => {
    setAdvancedReminderForm(false);

    methods.setValue("reminderWeekDays", []);
    methods.setValue("reminderEndAfterOccurrences", 1);
    methods.setValue("reminderEndDate", null);
  };

  const methods = useForm<FormValues>({
    defaultValues:
      mode === "edit"
        ? {
            eventDate: new Date(snapshotEvent!.event_date),
            eventTitle: snapshotEvent!.title,
            reminderInterval: snapshotEvent!.reminder_interval,
            reminderFrequency: snapshotEvent!.reminder_frequency,
            reminderEndAfterOccurrences: snapshotEvent!.reminder_end_after_occurrences,
            reminderWeekDays: snapshotEvent!.reminder_week_days,
            repeatReminder: false,
            emailSubject: snapshotEvent!.email_subject,
            emailMessage: snapshotEvent!.email_message,
            includeAttachment: snapshotEvent!.include_document_in_attachment,
            recipientsText: snapshotEvent!.recipients.map(({ email }) => email).join(","),
            recipients: snapshotEvent!.recipients.map(recipient => ({ ...recipient, checked: !recipient.is_readonly })),
            onePersonCompletion: snapshotEvent!.required_only_one_done,
            requiredOnlyOneDone: snapshotEvent!.required_only_one_done,
            sendReminderBefore: snapshotEvent!.reminder_start_date
              ? computeReminderOffset(
                  parseISO(snapshotEvent!.reminder_end_date ?? snapshotEvent!.event_date),
                  parseISO(snapshotEvent!.reminder_start_date),
                  snapshotEvent!.reminder_frequency as SnapshotEventReminderFrequency,
                )
              : null,
            sendReminderBeforeFrequency: snapshotEvent!.reminder_frequency as SnapshotEventReminderFrequency,
          }
        : {
            eventDate: eventDate || new Date(),
            eventTitle: "",
            repeatReminder: false,
            emailSubject: "",
            emailMessage: "",
            includeAttachment: false,
            recipientsText: "",
            recipients: [],
            onePersonCompletion: false,
            requiredOnlyOneDone: false,
            reminderInterval: 1,
            reminderFrequency: "DAILY",
            reminderEndAfterOccurrences: 1,
            sendReminderBefore: 1,
            sendReminderBeforeFrequency: "DAILY",
          },
  });

  const { fields: recipientFields } = useFieldArray({
    control: methods.control,
    name: "recipients",
    rules: { minLength: 1 },
  });

  // Fetch suggested event title when creating a new event with a businessReportOutcomeResultId
  React.useEffect(() => {
    if (mode === "create" && businessReportOutcomeResultId) {
      triggerSuggestName(businessReportOutcomeResultId);
    }
  }, [mode, businessReportOutcomeResultId, triggerSuggestName]);

  // Update the eventTitle field when the suggestion arrives
  React.useEffect(() => {
    if (suggestNameQuery.data) {
      methods.setValue("eventTitle", suggestNameQuery.data);
    }
  }, [suggestNameQuery.data, methods]);

  React.useEffect(() => {
    if (mode === "create" && snapshotEventMails.length > 0) {
      const defaultMail = snapshotEventMails.find(mail => mail.is_default) ?? snapshotEventMails[0];
      if (defaultMail) {
        methods.setValue("emailMessage", defaultMail.message);
      }
    }
  }, [mode, snapshotEventMails, methods]);

  const recipients = methods.watch("recipients");
  const onePersonCompletion = methods.watch("onePersonCompletion");
  const emailMessage = methods.watch("emailMessage");
  const checkedRecipientsCount = recipients.filter(r => r.checked).length;
  const shouldShowOnePersonCompletion = checkedRecipientsCount > 1;

  React.useEffect(() => {
    if (!shouldShowOnePersonCompletion && onePersonCompletion) {
      methods.setValue("onePersonCompletion", false);
    }
  }, [methods, onePersonCompletion, shouldShowOnePersonCompletion]);

  const onSubmit = async (data: FormValues) => {
    if (data.recipients.length === 0) {
      methods.setError("recipients", {
        type: "manual",
        message: "At least one recipient is required.",
      });
      return;
    }

    if (data.recipients.every(recipient => !recipient.checked)) {
      methods.setError("recipients", {
        type: "manual",
        message: "At least one recipient must be selected.",
      });
      return;
    }

    if (mode === "edit") {
      await updateSnapshotEventMutation({
        id: snapshotEvent!.id,
        ...buildEventPayload(data, new Date(data.reminderEndDate ?? data.eventDate)),
        reminder_end_after_occurrences: data.reminderEndAfterOccurrences ?? null,
      });
      toast.success("Event updated successfully");
      onClose();
      return;
    }

    analytics.snapshotCreateEvent();

    await createSnapshotEventMutation({
      ...buildEventPayload(data, new Date(data.eventDate)),
      status: "in_progress",
      reminder_end_after_occurrences: data.reminderEndAfterOccurrences,
      business_report_outcome_result_id: businessReportOutcomeResultId,
      business_report_outcome_id: businessReportOutcomeId,
    });
    toast.success("The event has been created.");
    onClose();
  };

  if (userQuery.isLoading) {
    return null;
  }

  if (userQuery.isError) {
    return <div>Error loading users</div>;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-h-[90vh] w-[1000px] max-w-[95vw] overflow-y-scroll rounded-lg bg-white p-6">
          <div className="mb-6 flex items-center justify-start gap-2">
            <DialogTitle className="text-2xl font-bold">Create Event & Email Alert</DialogTitle>
            <span className="text-md text-gray-700">{eventType}</span>
          </div>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
              <div className="flex flex-col gap-y-8 md:flex-row md:gap-x-8">
                <div className="flex-1 space-y-6">
                  {/* Event Date */}
                  <div className="space-y-2">
                    <Controller
                      control={methods.control}
                      name="eventDate"
                      render={({ field: { value: selected } }) => (
                        <div className="w-full">
                          <DatePicker
                            defaultValue={selected}
                            onChange={value => value && methods.setValue("eventDate", value)}
                            label="Event Date"
                          />
                        </div>
                      )}
                    />
                  </div>
                  {/* Event Title */}
                  <div className="space-y-2">
                    <Label htmlFor="event-title" className="block text-gray-700">
                      Event Title
                    </Label>
                    <div className="relative">
                      <input
                        id="event-title"
                        type="text"
                        placeholder={suggestNameQuery.isLoading ? "Loading suggestion..." : "Enter event title"}
                        disabled={suggestNameQuery.isLoading}
                        className={clsx(
                          "w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
                          {
                            "border-red-500": methods.formState.errors.eventTitle,
                            "bg-gray-100": suggestNameQuery.isLoading,
                          },
                        )}
                        {...methods.register("eventTitle", { required: true })}
                      />
                      {suggestNameQuery.isLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                        </div>
                      )}
                    </div>
                    {methods.formState.errors.eventTitle && (
                      <span className="text-sm text-red-600">This field is required</span>
                    )}
                  </div>

                  {/* Recipients */}
                  <div className="space-y-2">
                    <Label htmlFor="recipients" className="block text-gray-700">
                      Recipients
                    </Label>

                    <div className="space-y-4 rounded-md bg-ivory-mist-200 p-4">
                      <CreatableSelect
                        filterOption={(option, inputValue) =>
                          [
                            option.data?.name?.toLowerCase(),
                            option.data?.surname?.toLowerCase(),
                            option.data?.email?.toLowerCase(),
                          ].some(label => label && label.includes(inputValue.toLowerCase()))
                        }
                        getNewOptionData={(inputValue, label) =>
                          ({
                            label,
                            email: inputValue,
                            value: inputValue,
                            isNew: true,
                          }) as unknown as DirectoryUser
                        }
                        isMulti
                        options={
                          userQuery.data?.map(user => ({
                            ...user,
                            value: user.id,
                          })) ?? []
                        }
                        classNamePrefix="select"
                        isSearchable
                        name="recipients"
                        isClearable
                        defaultValue={recipients.map(item => userQuery.data?.find(({ email }) => item.email === email))}
                        components={
                          SelectComponents as Partial<
                            SelectComponentsType<DirectoryUser | undefined, true, GroupBase<DirectoryUser>>
                          >
                        }
                        styles={{
                          clearIndicator: base => ({
                            ...base,
                            display: "none",
                          }),
                          input: base => ({
                            ...base,
                            "input:focus": {
                              boxShadow: "none",
                            },
                          }),
                          valueContainer: provided => ({ ...provided, display: "flex" }),
                        }}
                        onChange={newValue => {
                          const nextRecipients = newValue.map(v => {
                            const checked =
                              recipients.find(recipient => recipient.email === v?.email)?.checked ?? false;

                            // @ts-expect-error label property is set for created recipient
                            return { email: v!.email ?? v!.label, checked };
                          });

                          if (nextRecipients.length === 1) {
                            nextRecipients[0].checked = true;
                          }

                          methods.setValue("recipients", nextRecipients);
                        }}
                      />

                      {recipients.length > 1 && (
                        <>
                          <div className="space-y-2">
                            <p className="text-gray-700">Select recipients who must complete an action:</p>
                            {recipientFields.map((recipient, index) => (
                              <div key={recipient.id} className="flex items-center gap-2">
                                <Controller
                                  control={methods.control}
                                  name={`recipients.${index}.checked`}
                                  render={({ field: { value, onChange } }) => (
                                    <Checkbox
                                      id={`recipient-${index}`}
                                      defaultChecked={value}
                                      onCheckedChange={checked => onChange(!!checked)}
                                      className="h-5 w-5 rounded border border-gray-300 bg-white"
                                    >
                                      <Check className="text-white" size={14} />
                                    </Checkbox>
                                  )}
                                />
                                <Label htmlFor={`recipient-${index}`}>{recipient.email}</Label>
                              </div>
                            ))}
                          </div>
                          <div className="rounded-md bg-gray-200 p-3 text-gray-600">
                            <span className="font-medium">Note:</span> All recipients will receive the email
                            notification. Only checked recipients will be required to complete an action.
                          </div>
                        </>
                      )}
                      {shouldShowOnePersonCompletion && (
                        <div className="rounded-md border border-orange-300 bg-orange-50 p-4">
                          <div className="flex items-center gap-2">
                            <Controller
                              control={methods.control}
                              name="onePersonCompletion"
                              render={({ field: { value, onChange } }) => (
                                <Checkbox
                                  id="one-person-completion"
                                  checked={value}
                                  onCheckedChange={checked => onChange(!!checked)}
                                  className="h-5 w-5 rounded border border-gray-300 bg-white"
                                >
                                  <Check className="text-white" size={14} />
                                </Checkbox>
                              )}
                            />
                            <Label htmlFor="one-person-completion" className="font-medium">
                              Only one person needs to complete this action
                            </Label>
                          </div>

                          <p className="ml-7 mt-2 text-gray-600">
                            If checked, the event will be marked as complete when any one of the selected recipients
                            completes their action, rather than requiring all selected recipients to complete it.
                          </p>
                        </div>
                      )}
                      {methods.formState.errors.recipients && (
                        <span className="text-red-600">At least on recipient is required</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 space-y-6">
                  {/* Reminders */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-gray-700">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="email-message" className="block text-gray-700">
                          Reminders
                        </Label>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={advancedReminderForm ? disableAdvancedReminderForm : enableAdvancedReminderForm}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      >
                        <Settings size={14} />
                        {advancedReminderForm ? "Simple" : "Custom"}
                      </Button>
                    </div>

                    {!advancedReminderForm ? (
                      <ReminderSimpleForm />
                    ) : (
                      <SnapshotEventReminderForm
                        initialRule={
                          mode === "edit"
                            ? {
                                endDate: new Date(snapshotEvent?.reminder_end_date ?? snapshotEvent!.event_date),
                                frequency: snapshotEvent!.reminder_frequency,
                                interval: snapshotEvent!.reminder_interval,
                                occurrences: snapshotEvent!.reminder_end_after_occurrences,
                                weekDays: snapshotEvent!.reminder_week_days ?? [],
                                endType:
                                  snapshotEvent!.reminder_end_after_occurrences > 0
                                    ? "AFTER_OCCURRENCES"
                                    : snapshotEvent!.reminder_end_date
                                      ? "ON_DATE"
                                      : "NEVER",
                              }
                            : {
                                endDate: eventDate,
                              }
                        }
                        onSave={rule => {
                          methods.setValue("reminderWeekDays", rule.weekDays);
                          methods.setValue("reminderFrequency", rule.frequency);
                          methods.setValue("reminderInterval", rule.interval);
                          methods.setValue("reminderEndAfterOccurrences", rule.occurrences);
                          if (rule.endDate) {
                            methods.setValue("reminderEndDate", rule.endDate.toDateString());
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Email Message */}
                  <div className="min-h-[30rem] space-y-2">
                    <Label htmlFor="email-message" className="block text-gray-700">
                      Email Message
                    </Label>
                    <RichTextEditor
                      value={emailMessage}
                      onChange={value => methods.setValue("emailMessage", value)}
                      height="25rem"
                    />

                    {methods.formState.errors.emailMessage && (
                      <span className="text-sm text-red-600">This field is required</span>
                    )}
                  </div>

                  {/* Include document */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="include-document"
                      className="mt-0.5 h-5 w-5 rounded border border-gray-300 bg-white"
                      defaultChecked={snapshotEvent?.include_document_in_attachment}
                      onCheckedChange={checked => methods.setValue("includeAttachment", !!checked)}
                    >
                      <Check className="text-white" size={14} />
                    </Checkbox>
                    <div>
                      <Label htmlFor="include-document" className="block text-gray-700">
                        Include document as attachment
                      </Label>
                      <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                        {snapshotEvent?.include_document_in_attachment && (
                          <>
                            <span>Document {snapshotEvent.document_name} will be attached to the email</span>
                            <Paperclip size={14} />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <DialogFooter className="flex justify-end gap-3 border-t pt-4">
                <Button type="button" variant="outline" onClick={() => setIsNotesModalOpen(true)}>
                  Notes
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">{mode === "create" ? "Create" : "Update"} Event</Button>
              </DialogFooter>
            </form>
          </FormProvider>
          <SnapshotEventMailModal
            open={isNotesModalOpen}
            onClose={() => setIsNotesModalOpen(false)}
            applyNoteFn={value => {
              methods.setValue("emailMessage", value);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
